import {Service} from '../../../providers/service/service';
import {service} from '../../../providers';
import {LanguageContextService} from '../../language';
import {RepositoryContextService} from '../repository';
import {OntoToastrService} from '../../toastr';
import {ShepherdService} from './shepherd.service';
import {InteractiveGuide} from '../../../models/interactive-guide/interactive-guide';
import {WindowService} from '../../window';
import {GuidePlugin, InteractiveGuideExtensionPoint} from '../../../models/plugins';
import {GuideApi} from './guide-api';
import {GuideContextService} from './guide-context.service';
import {GuidesRestService} from './guides-rest.service';
import {mapInteractiveGuideListResponseToModel} from './mappers/interactive-guide.mapper';

export class GuidesService implements Service {
  private readonly shepherdService = service(ShepherdService);
  private readonly toastrService = service(OntoToastrService);
  private readonly repositoryContextService = service(RepositoryContextService);
  private readonly guideApi = service(GuideApi);
  private readonly guideContextService = service(GuideContextService);
  private readonly guidesRestService = service(GuidesRestService);

  /**
   * Creates a guide and starts it from <code>startStepId</code> if provided. Starts from the beginning otherwise.
   *
   *
   * Steps:
   * 1. Shows an error toast if the guide is not found;
   * 2. Adds index incrementation to the guide repository ID if it already exists in the repository context to ensure uniqueness;
   * 3. Converts complex steps to core steps;
   * 4. Starts or resumes the guide via ShepherdService.
   *
   * @param guide - The guide to start.
   * @param services the services passed to the steps
   * @param startStepId - The step ID to start from. If defined, the guide will be resumed from that step.
   * @param isAutoStarted - Whether the guide was automatically started.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startGuide(guide: InteractiveGuide | undefined, services: Record<string, any>, startStepId?: string, isAutoStarted = false): void {
    const bundle = service(LanguageContextService).getLanguageBundle();
    if (!guide) {
      this.toastrService.error(this.guideApi.translate(bundle, 'guides.error.guide-not-found'));
      return;
    }

    const repositoryIdBase = guide.getRepositoryIdBase();

    if (repositoryIdBase) {
      // repositoryIdBase in the options can be used as a template to find a free repository ID.
      // For example, setting repositoryIdBase to 'myrepo' will find the first free ID from:
      // - myrepo
      // - myrepo2
      // - myrepo3 and so on
      const repositoryList = this.repositoryContextService.getRepositoryList();
      guide.setRepositoryId(repositoryIdBase);
      const MAX_ATTEMPTS = 1000;
      for (let i = 2; i <= MAX_ATTEMPTS && repositoryList.findRepository(guide.getRepositoryId()!, '', true); i++) {
        guide.setRepositoryId(repositoryIdBase + i);
      }
    }

    guide.setTranslatedGuideName(this.guideApi.translate(bundle, guide.getName()));
    guide.setTranslatedGuideDescription(this.guideApi.translate(bundle, guide.getDescription()));

    const guidePlugins = WindowService.getPluginRegistry().get<GuidePlugin>(InteractiveGuideExtensionPoint.NAME);
    const stepDescriptions =  guide.toStepsDescriptions(guidePlugins, services);

    stepDescriptions.forEach((step, index) => {
      step.id = index;
      if (step.scrollOffset) {
        step.scrollToHandler = () => this.guideApi.scrollToOffset(step.scrollOffset!);
      }
    });

    this.guideContextService.updateHasRunningGuide(true);

    const unsubscribeOnGuideEnd = this.shepherdService.subscribeOnGuideEnd(() => {
      this.guideContextService.updateHasRunningGuide(false);
      unsubscribeOnGuideEnd();
    });

    if (startStepId) {
      this.shepherdService.resumeGuide(guide.getId(), stepDescriptions, startStepId);
    } else {
      this.shepherdService.startGuide(guide.getId(), stepDescriptions, startStepId, isAutoStarted);
    }

    this.guideContextService.updatePaused(false);
  }

  /**
   * Fetches the list of all available guides and maps them to models.
   *
   * @returns A Promise that resolves to an array of {@link InteractiveGuide} objects.
   */
  async getGuides(): Promise<InteractiveGuide[]> {
    const guides = await this.guidesRestService.getGuides();
    return mapInteractiveGuideListResponseToModel(guides);
  }

  /**
   * Automatically starts a guide by its ID.
   * Fetches all guides, finds the one matching the given ID, and starts it.
   * If the ID is a number, it is coerced to a string before matching.
   *
   * @param autoStartGuideId - The ID of the guide to auto-start.
   * @param services - The services passed to the guide steps.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  autoStartGuide(autoStartGuideId: string | number, services: Record<string, any>): void {
    if (!autoStartGuideId) {
      return;
    }

    const guideId = String(autoStartGuideId);

    this.getGuides().then((guides: InteractiveGuide[]) => {
      const selectedGuide = guides.find((guide) => guide.getId() === guideId);
      if (selectedGuide) {
        this.startGuide(selectedGuide, services, undefined, true);
      }
    });
  }

  /**
   * Returns whether scrolling is allowed during the current guide state.
   * Scrolling is allowed when no guide is active or when the current step explicitly permits it.
   */
  isScrollingAllowed(): boolean {
    return this.shepherdService.isScrollingAllowed();
  }

  /**
   * Returns whether the guide is currently active.
   */
  isActive(): boolean {
    return this.shepherdService.isActive();
  };
}
